import { blueGrey, grey, red } from '@mui/material/colors'
import { alpha, createTheme } from '@mui/material/styles'
import {
  accordionBaseStyles,
  accordionDetailsStyles,
  accordionSummaryBaseStyles,
  autocompleteBaseStyles,
  breakpointValues,
  cardBaseStyles,
  collapseStyles,
  dataGridCheckboxStyles,
  dialogPaperStyles,
  formControlStyles,
  formLabelStyles,
  gridBaseStyles,
  hiddenSearchColumnStyles,
  inputBaseInputStyles,
  inputLabelOutlinedStyles,
  menuStyles,
  paperBaseStyles,
  quillDarkStyles,
  quillLightStyles,
  selectBaseStyles,
  tableContainerStyles,
  tableStyles,
  themeScrollbarStyles,
  tooltipBaseStyles,
} from '../tokens/components'

const gradient = 'linear-gradient(135deg, #ffdbdb 0%, #b1eaff 100%)'
const grid_gradient = 'linear-gradient(135deg, #ffdbdb 0%, #b1eaff 40%)'
const ai_gradient = 'linear-gradient(135deg, #dacaca 0%, #93bbca 40%)'
const dots = 'radial-gradient(circle, rgba(0,0,0,0.2) 1px, transparent 1px)'
const fallback = 'rgba(255,255,255,1)'

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      lighter: blueGrey[100],
      light: blueGrey[300],
      main: 'rgba(114, 112, 114, 0.7)',
      dark: blueGrey[700],
      darker: blueGrey[900],
      darkest: 'black',
      contrastText: '#000000',
    },
    background: {
      image: dots,
      divider: 'rgb(180, 213, 229)',
      default: fallback,
      defaultdim: 'rgba(236, 239, 248, 0.5)',
      section: 'rgb(255, 255, 255)',
      header: 'rgb(232, 240, 254)',
      main: 'rgba(255,255,255,1)',
      paper: 'rgba(255,255,255,1)',
      disabled: 'rgba(255,255,255,0.3)',
      mentions: 'rgba(179, 178, 178, 0.2)',
      tableRow: 'rgba(255,255,255,1)',
      input: 'rgb(245, 245, 245)',
      sectionContent: 'rgb(215, 228, 234)',
      selected: 'rgb(232, 240, 254)',
      timelineGrid: 'rgb(236, 239, 248)',
      help: 'rgb(236, 239, 248)',
      tooltip: 'rgba(255, 255, 255, 0.87)',
      reviewContent: 'rgba(161, 215, 235, 1)',
      loading: 'rgb(107, 185, 240)',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      isDragging: '0 4px 6px rgba(0, 0, 0, 0.2)',
      overlay: 'rgba(255,255,255, 0)',
      thumbnail: 'rgba(202, 65, 92, 0.8)',
      details: 'rgb(201, 216, 223)',
      scrollbar_thumb: 'rgba(167,51,51,1)',
      scrollbar_hover: 'rgb(122, 121, 121)',
      fielddisabled: 'rgb(47, 53, 65)',
      dialogtext: 'rgb(236, 239, 248)',
      toolbar: 'rgba(206, 220, 250, 1)',
      toolbarlight: 'rgba(206, 220, 250, 0.2)',
      gradient: gradient,
      gridgradient: grid_gradient,
      ai_agent: ai_gradient,
      snackbar: '',
      postdated: 'rgba(90, 111, 228, 0.48)',
      button: 'rgb(33, 100, 160)',
    },
    text: {
      primary: 'rgba(0,0,0,1)',
      // highlight: 'rgb(107, 185, 240)',
      highlight: '#6BB9F0',
      ai_agent_text: '#546E7A',
      shadow: 'rgba(255,255,255,0.5)',
      subtask: 'rgb(21, 112, 231)',
      secondary: 'rgba(0,0,0,0.5)',
      disabled: 'rgba(0,0,0,0.2)',
      tertiary: 'rgba(0,0,0,0.3)',
      resetFilter: 'rgb(255, 165, 93)',
      chip: 'rgba(0,0,0,1)',
      admin: 'rgba(255, 123, 187, 1)',
      success: 'rgb(80, 200, 120)',
      successdim: 'rgba(129, 230, 163, 1)',
      warning: 'rgb(249, 167, 49)',
      error: 'rgba(220, 20, 60)',
      errordim: 'rgb(223, 144, 144)',
      link: 'rgb(46, 55, 174)',
      revise: 'rgb(255, 131, 88)',
      review: 'rgb(33, 201, 223)',
      htmlText: '#333333',
      parent: 'rgba(67, 85, 241, 1)',
      size: 12,
    },
    badge: {
      dot: 'rgb(255, 165, 93)',
    },
    chip: {
      sizeSmall: { height: 15, width: 15 },
    },
    card: {
      default: 'rgb(236, 239, 248)',
    },
    icon: {
      complete: 'rgb(5, 190, 113)',
      warning: 'rgb(249, 167, 49)',
      decline: 'rgba(220, 20, 60, 0.8)',
      teams: 'rgb(99, 102, 241)',
      deals: '#fbc11e',
      organizations: 'rgb(114, 229, 218)',
      approvals: 'rgba(205, 254, 194, 1)',
      contacts: 'rgb(255, 123, 187)',
      nco: 'rgb(52, 211, 145)',
      accounting: 'rgb(107, 185, 240)',
      teamtasks: 'rgb(247, 63, 236)',
      creative: 'rgb(111, 31, 223)',
      seo: 'rgb(238, 111, 141)',
      mtb: 'rgb(20, 119, 248)',
      products: 'rgb(224, 110, 215)',
      website: 'rgb(84, 126, 240)',
      campaignChecklist: 'rgb(255, 131, 88)',
      projects: 'rgb(245, 108, 53)',
      insight: 'rgb(65, 190, 169)',
      observation: 'rgb(168, 202, 255)',
      leasingPercentage: 'rgba(230, 181, 21, 1)',
      pandadoc: 'rgb(36, 133, 103)',
      dashboard: 'rgb(5, 190, 113)',
    },
    secondary: {
      light: '#293742',
      main: grey[50],
      dark: grey[50],
      darker: red[900],
    },
    tertiary: {
      lighter: grey[50],
      light: grey[100],
      main: grey[400],
      dark: grey[700],
      darker: grey[900],
    },
  },
  typography: {
    fontFamily: '"Funnel Sans", sans-serif',
    fontSize: 14,
    h1: {
      fontSize: '2.5rem',
    },
    h2: {
      color: 'rgba(0,0,0,1)',
    },
    h6: {
      color: 'rgba(0,0,0,1)',
    },
    body1: {
      color: 'rgba(0,0,0,1)',
    },
  },
  spacing: 8,
  hover: 'rgba(0,0,0, 0.7)',
  breakpoints: {
    values: breakpointValues,
  },
  shadows: Array(25)
    .fill('none')
    .map((_, index) =>
      index === 0
        ? 'none'
        : `0px ${index}px ${index * 2}px rgba(0, 0, 0, 0.14),
    0px ${index / 2}px ${index}px rgba(0, 0, 0, 0.12),
    0px ${index}px ${index * 3}px rgba(0, 0, 0, 0.2)`
    ) as any,

  components: {
    MuiCssBaseline: {
      styleOverrides: theme => ({
        body: {
          backgroundImage: `${theme.palette.background.gradient}`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        },
      }),
    },
    MyCustomQuill: {
      light: quillLightStyles,
      dark: quillDarkStyles,
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...paperBaseStyles(theme),
          backgroundImage: 'none',
        }),
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {},
      },
    },
    MuiCollapse: {
      styleOverrides: collapseStyles,
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: 'black',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            color: 'rgb(92, 179, 253)',
            backgroundColor: `${alpha(theme.palette.background.mentions, 0.25)} !important`,
            fontWeight: 'bold',
          },
          '&.MuiTab-textColorPrimary.Mui-disabled': {
            color: 'rgba(0,0,0,0.4) !important',
          },
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderColor: 'black',
          color: 'black',
          '&:hover': {
            backgroundColor: grey[400],
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: tableStyles,
    },
    MuiTableContainer: {
      styleOverrides: tableContainerStyles,
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: blueGrey[100],
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {},
      },
    },
    MuiMenu: {
      styleOverrides: menuStyles,
    },
    MuiList: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...themeScrollbarStyles(theme),
        }),
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...themeScrollbarStyles(theme),
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'rgb(0,0,0,0.5)',
          '&.Mui-focused': {
            color: 'black',
          },
        },
        outlined: inputLabelOutlinedStyles,
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        option: {
          height: 'auto',
          '&:hover': {
            backgroundColor: 'f5f5f5',
            cursor: 'pointer',
          },
        },
        input: autocompleteBaseStyles.input,
        root: autocompleteBaseStyles.root,
        popper: autocompleteBaseStyles.popper,
        paper: {
          ...autocompleteBaseStyles.paper,
          width: 'auto',
        },
        inputRoot: {
          '& .MuiOutlinedInput-root': {
            '& .MuiOutlinedInput-notchedOutline': {
              color: 'rgba(0,0,0,1)',
              borderWidth: '1px',
              padding: '0px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'blue',
              borderWidth: '2px',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(97, 229, 155, 0.5)',
              borderWidth: '2px',
            },
          },
        },
        endAdornment: {
          display: 'none',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {},
        input: {
          ...inputBaseInputStyles,
          color: 'black',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: formControlStyles,
    },
    MuiFormLabel: {
      styleOverrides: formLabelStyles,
    },
    MuiTextField: {
      styleOverrides: {
        root: {},
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          color: 'black',
          ...selectBaseStyles,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          [`&.selectMenu .MuiMenuItem-root`]: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          },
          '&.Mui-selected': {
            backgroundColor: `${alpha(theme.palette.background.defaultdim, 0.75)} !important`,
          },
          '&.Mui-selected:hover': {
            backgroundColor: `${alpha(theme.palette.background.defaultdim, 0.2)} !important`,
          },
        }),
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          ...accordionBaseStyles,
          backgroundColor: '#ECEFF8',
          color: grey[200],
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        ...accordionSummaryBaseStyles,
        root: {
          ...accordionSummaryBaseStyles.root,
          backgroundColor: 'rgba(255,255,255,1)',
          color: 'rgba(255,255,255,1)',
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          ...accordionDetailsStyles.root,
          backgroundColor: 'rgba(255,255,255,1)',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            boxShadow: 'inset 0 0 6px rgba(0,0,0,0.3)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(8, 8, 8, 1)',
            borderRadius: '6px',
            '&:hover': {
              backgroundColor: 'text.secondary',
            },
          },
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          color: 'rgba(0,0,0,1)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          ...tooltipBaseStyles,
          backgroundColor: 'rgba(255,255,255,0.9)',
          color: 'black',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: dialogPaperStyles,
      },
    },
    MuiGrid: {
      styleOverrides: {
        ...gridBaseStyles,
        root: {
          ...gridBaseStyles.root,
          transition: 'background-color 0.3s ease',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          ...cardBaseStyles,
          backgroundColor: 'rgba(255,255,255,1)',
          color: '#fafafa',
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: '#000',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          color: 'black',
          borderColor: 'black',
          '& .MuiChip-deleteIconSmall': {
            color: 'black',
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: 0,
          maxHeight: '100vh',
          maxWidth: '100vw',
          fontFamily: '"Funnel Sans", sans-serif',
          backgroundImage: 'none',
          background: 'transparent',
          borderColor: 'rgba(0,0,0,0.4)',
          // marginTop: '10px',
          '& .MuiDataGrid-cell': {
            backgroundColor: 'inherit',
            color: 'rgba(0, 0, 0)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '& > *': {
              transition: 'transform 0.3s ease',
            },
            '&:hover > *': {
              transform: 'scale(1.07)',
            },
          },
          // Scrollbar styles
          '& .MuiDataGrid-scrollbar': {
            scrollbarWidth: 'auto',
            scrollbarColor: `${theme.palette.text.primary} ${theme.palette.background.default}`,
          },
          '&': {
            scrollbarColor: `${theme.palette.text.primary} ${theme.palette.background.default}`,
          },
          // Disable cell hover transform for cells containing avatars - let EntityAvatar handle its own hover
          '& .MuiDataGrid-cell:has(.MuiAvatar-root), & .MuiDataGrid-cell:has(.MuiAvatarGroup-root)': {
            overflow: 'visible !important', // Allow avatars to scale without being clipped
          },
          '& .MuiDataGrid-cell:has(.MuiAvatar-root):hover, & .MuiDataGrid-cell:has(.MuiAvatarGroup-root):hover': {
            transform: 'none',
            backgroundColor: 'inherit',
          },
          '& .MuiDataGrid-columnHeaders': {
            borderColor: 'rgba(0,0,0,0.4)',
            fontSize: '14px',
            backgroundColor: 'rgb(236, 239, 248) !important',
            color: 'rgba(0, 0, 0)',
          },
          '& .MuiDataGrid-row': {
            fontSize: '12px',
            backgroundColor: theme.palette.background.section,
            '&:hover': {
              backgroundColor: theme.palette.background.section,
            },
          },
          '& .MuiDataGrid-row.Mui-selected': {
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
            },
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          },
          ...dataGridCheckboxStyles,
          '& .MuiCheckbox-root svg': {
            ...dataGridCheckboxStyles['& .MuiCheckbox-root svg'],
            border: '1px solid rgba(0, 0, 0, 1)',
          },
          '& .MuiDataGrid-toolbarContainer': {
            backgroundColor: 'rgb(236, 239, 248)',
            overflow: 'visible',
            position: 'relative',
            zIndex: 10,
          },
          '& .MuiDataGrid-toolbarContainer .MuiToolbar-root': {
            overflow: 'visible',
            paddingTop: '12px',
            minHeight: '56px',
          },
          '& .MuiDataGrid-container--top [role=row]': {
            backgroundColor: 'rgb(236, 239, 248)',
            borderLeft: '4px solid transparent',
            borderRight: '4px solid transparent',
            borderTop: '4px solid rgb(255,255,255)',
          },
          // Aggregation row styling
          '& .MuiDataGrid-row[data-id="auto-generated-group-footer-root"]': {
            backgroundColor: `${theme.palette.background.defaultdim} !important`,
          },
          '& .MuiDataGrid-row[data-id="auto-generated-group-footer-root"] .MuiDataGrid-cell': {
            backgroundColor: 'inherit !important',
          },
          '& .MuiDataGrid-row--lastVisible[data-id="auto-generated-group-footer-root"]': {
            zIndex: 100,
            backgroundColor: theme.palette.background.defaultdim,
            width: '100%',
            pointerEvents: 'none',
          },
          // Pinned columns background
          '& .MuiDataGrid-pinnedColumns--left, & .MuiDataGrid-pinnedColumns--right': {
            backgroundColor: theme.palette.background.defaultdim,
          },
          '& .MuiDataGrid-pinnedColumns--left .MuiDataGrid-cell, & .MuiDataGrid-pinnedColumns--right .MuiDataGrid-cell': {
            backgroundColor: 'inherit',
          },
          // Fix pinned column background in aggregation row
          '& .MuiDataGrid-pinnedColumns .MuiDataGrid-row[data-id="auto-generated-group-footer-root"], & .MuiDataGrid-pinnedColumnHeaders--left, & .MuiDataGrid-pinnedColumnHeaders--right': {
            backgroundColor: theme.palette.background.paper,
          },
          // Virtual scroller backgrounds
          '& .MuiDataGrid-virtualScrollerContent, & .MuiDataGrid-virtualScrollerRenderZone': {
            backgroundColor: 'transparent',
          },
          // Bottom container styling
          '& .MuiDataGrid-container--bottom': {
            padding: '0 !important',
            margin: '0 !important',
          },
          '& .MuiDataGrid-scrollArea--bottom': {
            padding: '0 !important',
            margin: '0 !important',
          },
          '& .MuiDataGrid-filler--pinnedRight': {
            backgroundColor: `transparent !important`,
          },
          '& .MuiDataGrid-filler--pinnedLeft': {
            backgroundColor: `transparent !important`,
          },
          // Bottom pinned rows styling
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row, & .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-cell': {
            backgroundColor: `${theme.palette.background.paper} !important`,
          },
          // Server aggregation row styling
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row[data-id="__SERVER_AGGREGATIONS_ROW__"]': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            fontWeight: 600,
          },
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row[data-id="__SERVER_AGGREGATIONS_ROW__"] .MuiDataGrid-cell': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            pointerEvents: 'none',
          },
          // Disable hover effects on server aggregation row
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row[data-id="__SERVER_AGGREGATIONS_ROW__"]:hover': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            cursor: 'default',
          },
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row[data-id="__SERVER_AGGREGATIONS_ROW__"]:hover .MuiDataGrid-cell': {
            backgroundColor: `${theme.palette.background.paper} !important`,
          },
          // Disable cell hover effects on aggregation row
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row[data-id="__SERVER_AGGREGATIONS_ROW__"] .MuiDataGrid-cell:hover': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            cursor: 'default',
          },
          // ============================================================================
          // Row Classes - Special row styling
          // ============================================================================
          '& .high-priority-row': {
            backgroundColor: 'rgba(255, 0, 0, 0.1) !important',
          },
          '& .postdated': {
            backgroundColor: 'rgba(255, 193, 7, 0.15) !important',
          },
          '& .postdated:hover': {
            backgroundColor: 'rgba(255, 193, 7, 0.15) !important',
          },
          '& .postdated .MuiDataGrid-cell': {
            backgroundColor: 'inherit !important',
          },
          '& .postdated .MuiDataGrid-cell:hover': {
            backgroundColor: 'inherit !important',
          },
          '& .MuiDataGrid-row.highlighted-row': {
            opacity: '1 !important',
            backgroundColor: 'rgba(201, 52, 52, 0.2) !important',
            zIndex: '1500 !important',
          },
          '& .MuiDataGrid-row.Mui-selected.MuiDataGrid-row--firstVisible.MuiDataGrid-row--lastVisible.highlighted-row': {
            opacity: '1 !important',
            backgroundColor: 'rgba(228, 22, 22, 0.2) !important',
            zIndex: '1500 !important',
          },
          '& .task-row': {
            backgroundColor: '#f3f4f6',
            fontWeight: 600,
            borderTop: '1px solid #ddd',
          },
          '& .subtask-row': {
            backgroundColor: theme.palette.background.default,
            paddingLeft: '32px',
          },
          '& .disabled-selection': {
            opacity: '0.3 !important',
            pointerEvents: 'none',
            backgroundColor: 'rgba(100, 100, 100, 0.05) !important',
          },
          '& .product-row': {
            backgroundColor: 'rgba(100, 100, 100, 0.05)',
          },
          '& .MuiDataGrid-row.delete-row': {
            border: '1px solid',
            borderColor: 'rgb(238, 124, 124) !important',
            transition: 'background-color 0.3s ease',
            zIndex: '1900 !important',
            position: 'relative !important',
          },
          // ============================================================================
          // Org Name Header Styling
          // ============================================================================
          '& .org-name-header .MuiDataGrid-columnHeaderTitleContainer': {
            flex: 1,
            minWidth: 0,
          },
          '& .org-name-header .MuiDataGrid-columnHeaderTitleContainerContent': {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            minWidth: 0,
          },
          '& .org-name-header .MuiDataGrid-iconButtonContainer': {
            flexShrink: 0,
          },
          '& .org-name-header .MuiStack-root': {
            display: 'flex',
            flex: '1 1 auto',
            minWidth: 0,
          },
          '& .org-name-header .MuiStack-root > .MuiTypography-root': {
            flex: '1 1 auto',
            minWidth: 0,
          },
          '& .org-name-header .MuiStack-root > .MuiBox-root': {
            marginLeft: 'auto',
            display: 'flex',
            gap: '8px',
            flex: '0 0 auto',
          },
          // ============================================================================
          // Pinned Rows Top Styling
          // ============================================================================
          '& .MuiDataGrid-pinnedRows, & .MuiDataGrid-pinnedRows--top': {
            borderRadius: '0 !important',
            padding: '0 !important',
            margin: '0 !important',
            border: '0 !important',
          },
          '& .MuiDataGrid-pinnedRows--top .MuiDataGrid-row, & .MuiDataGrid-pinnedRows--top .MuiDataGrid-cell': {
            borderRadius: '0 !important',
            backgroundColor: theme.palette.background.paper,
          },
          '& .MuiDataGrid-container--top': {
            padding: '0 !important',
            margin: '0 !important',
            border: '0 !important',
            overflow: 'visible',
          },
          '& .MuiDataGrid-container--top [role="row"]': {
            border: '0 !important',
          },
          '& .MuiDataGrid-pinnedRows--top': {
            paddingBottom: '22px',
          },
          '& .MuiDataGrid-pinnedRows--top .MuiDataGrid-cell': {
            paddingTop: '2px',
            paddingBottom: '2px',
          },
          '& .MuiDataGrid-pinnedRows--top .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-groupingCriteriaCellToggle': {
            display: 'none !important',
            pointerEvents: 'none',
          },
          '& .MuiDataGrid-pinnedRows--top .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-groupingCriteriaCell, & .MuiDataGrid-pinnedRows--top .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-cellContent': {
            marginLeft: '0 !important',
            paddingLeft: '0 !important',
          },
          // ============================================================================
          // Pinned Rows Bottom - Additional Styling
          // ============================================================================
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-virtualScrollerRenderZone, & .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-virtualScrollerContent': {
            backgroundColor: `${theme.palette.background.paper} !important`,
          },
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-groupingCriteriaCellToggle': {
            display: 'none !important',
            pointerEvents: 'none',
          },
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-groupingCriteriaCell, & .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-cellContent': {
            marginLeft: '0 !important',
            paddingLeft: '0 !important',
          },
          // ============================================================================
          // Drawer Open State - Dim rows except current
          // ============================================================================
          '&.has-drawer-open .MuiDataGrid-row': {
            position: 'relative',
            opacity: 0.9,
          },
          '&.has-drawer-open .MuiDataGrid-row:not(.drawer-current-row)::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.2)',
            pointerEvents: 'none',
          },
          '&.has-drawer-open .drawer-current-row': {
            isolation: 'isolate',
            zIndex: 1,
            opacity: '1 !important',
          },
          '&.has-drawer-open .drawer-current-row .MuiDataGrid-cell': {
            backgroundColor: 'rgba(25, 118, 210, 0.12)',
          },
          '&.has-drawer-open .MuiDataGrid-columnHeaders, &.has-drawer-open .MuiDataGrid-toolbarContainer, &.has-drawer-open .MuiDataGrid-footerContainer, &.has-drawer-open .MuiDataGrid-toolbar, &.has-drawer-open .MuiDataGrid-cellCheckbox': {
            opacity: 0.1,
            backgroundColor: 'transparent',
            pointerEvents: 'none',
          },
          '&.has-drawer-open .MuiDataGrid-row:hover': {
            opacity: 1,
            backgroundColor: 'rgba(25, 118, 210, 0.08) !important',
          },
          // ============================================================================
          // Mass Update Highlighting - Editable cells when multiple rows selected
          // ============================================================================
          '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell.mass-update-target': {
            backgroundColor: 'rgba(24, 144, 255, 0.08)',
            borderLeft: '2px solid rgba(24, 144, 255, 0.4)',
          },
          '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell.mass-update-target:hover': {
            backgroundColor: 'rgba(24, 144, 255, 0.15)',
          },
          '& .MuiDataGrid-cell--editing.mass-update-target': {
            backgroundColor: 'rgba(24, 144, 255, 0.2) !important',
            boxShadow: 'inset 0 0 0 2px rgba(24, 144, 255, 0.5)',
          },
          // ============================================================================
          // NCO Expand Button
          // ============================================================================
          '& .nco-expand-button': {
            pointerEvents: 'var(--nco-expand-events) !important',
            color: 'var(--nco-expand-color) !important',
          },
          ...hiddenSearchColumnStyles,
        }),
        pagination: ({ theme }) => ({
          color: theme.palette.text.secondary,
        }),
        footerContainer: ({ theme }) => ({
          backgroundColor: theme.palette.background.defaultdim,
          '& .MuiTablePagination-root': {
            backgroundColor: 'transparent',
          },
          '& .MuiTablePagination-toolbar': {
            backgroundColor: 'transparent',
          },
          '& .MuiToolbar-root': {
            backgroundColor: 'transparent',
          },
        }),
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgb(255, 255, 255)',
        },
      },
    },
  },
})
